import React, { useCallback } from 'react';

import { useMutateOutbox } from '../../../../../fetchHooks/useOutbox';
import type { Account } from '../../../../../generated-types';
import { usePaymentAccountContext } from '../../../../../hooks/useAccountState';
import { useSigningContext } from '../../../../../signingContext/signingContext';
import { Actions } from '../../../../../signingContext/signingTypes';
import requireMultipleApprovers from '../../../../../utils/boolean/requireMultipleApprovers';
import TransactionResultInfo from '../../../TransactionResultInfo';
import { PaymentActionType } from '../../form/actions';
import createPaymentRequest from '../../form/createPaymentRequest';
import { PaymentState } from '../../form/state';
import useFormContext from '../../form/useFormContext';
import useSetFromAccountIfUndefined from '../../hooks/useSetFromAccountIfUndefined';

import '../../../TransactionModal.scss';

export interface Props {
    onClose: () => void;
    account: Account | undefined;
}

const PaymentFormResult: React.FC<Props> = ({ onClose, account }) => {
    const { state, handleChange, redirectLinkAvtalegiro } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();

    const { startSigning, signingStatus, signingError, handleCancelSigningFlow, dispatch, trackSigningEvent } =
        useSigningContext(createPaymentRequest());
    const { error: createOutboxError, reset } = useMutateOutbox();

    const createPaymentWithDuplicateOverride = useCallback(() => {
        trackSigningEvent('create_payment', 'duplicate');
        startSigning({ ...state, acceptDuplicate: true });
    }, [startSigning, state, trackSigningEvent]);

    const fromAccountId = useSetFromAccountIfUndefined<PaymentState>(
        state,
        (account) =>
            handleChange({
                type: PaymentActionType.FROM_ACCOUNT_SELECTED,
                payload: account.accountNumber,
            }),
        account,
    );

    // eslint-disable-next-line no-nested-ternary
    const retry = signingError ? handleCancelSigningFlow : createOutboxError ? reset : onClose;

    const requireAdditionalSigning = requireMultipleApprovers(targetFromAccount ?? account);
    return (
        <TransactionResultInfo
            type="PAYMENT"
            status={signingStatus}
            error={signingError || createOutboxError}
            retry={retry}
            fromAccountId={fromAccountId}
            onClose={onClose}
            onRetryManually={() => dispatch({ type: Actions.CLEAR_ERROR })}
            onContinue={() => {
                dispatch({ type: Actions.CLEAR_ERROR });
                createPaymentWithDuplicateOverride();
            }}
            paymentDate={state.paymentDate}
            redirectLinkAvtalegiro={redirectLinkAvtalegiro}
            isBeingSentForApproval={requireAdditionalSigning}
        />
    );
};

export default PaymentFormResult;
